import {DEPLOYED_LANGUAGES, languageBrandOf} from "../deployedTopology.mjs";
import type {Locale} from "../i18n/localePreference";
import {SITE_LOCALES} from "../seo/siteMeta";

/**
 * The locales the language picker offers on a page whose document is published in `documentLocale`
 * (kitecorp/transferify-ui#3937): every market, and a language only on the pages of the brand it belongs to.
 *
 * French is a language of the main brand, so `transferify.eu` and its `/fr` pages list it, while
 * `transferify.ro` and the `/de` storefront, whose readers did not come for the main brand's languages, do not.
 * Asked of the DOCUMENT's locale (host or forced tree, never the reader's persisted choice), the way every
 * identity decision here is (#1135): a Romanian reader who once chose French must not make the Romanian site
 * start offering it.
 *
 * React-free (the `logic-outside-react` rule): {@link ./useLanguageChoices.useLanguageChoices} is a thin caller.
 *
 * @param locales the candidates, in the order to list them; the locales this build ships by default
 * @param languages the registry's languages, defaulting to the deployed ones
 */
export function pickerLocales<L extends string = Locale>(
	documentLocale: string,
	// The default is the app's own locale list; a caller that passes its own list (a test adding a fifth
	// language) types the result by that list.
	locales: readonly L[] = SITE_LOCALES as readonly string[] as readonly L[],
	languages: ReadonlyArray<{locale: string; brand: string}> = DEPLOYED_LANGUAGES,
): L[] {
	const pageBrand = languageBrandOf(documentLocale, undefined, languages);
	return locales.filter((locale) => {
		const brand = languageBrandOf(locale, undefined, languages);
		return brand === locale || brand === pageBrand;
	});
}
